import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import { CreditCard, Cancel, QrCode2, Link as LinkIcon } from '@mui/icons-material';
import { Facture, StatutFacture, StripePaymentStatus } from '../types';
import paymentService from '../services/paymentService';
import PaymentLinkDisplay from './PaymentLinkDisplay';

interface PaymentButtonProps {
  facture: Facture;
  onPaymentCreated?: () => void;
}

const PaymentButton: React.FC<PaymentButtonProps> = ({ facture, onPaymentCreated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [currentPaymentUrl, setCurrentPaymentUrl] = useState<string>('');

  // Vérifier si un paiement peut être créé
  const canCreatePayment =
    facture.statut !== StatutFacture.PAYEE &&
    facture.statut !== StatutFacture.ANNULEE &&
    !facture.stripePaymentUrl;

  // Vérifier si un paiement est en attente
  const hasPendingPayment =
    facture.stripePaymentUrl &&
    facture.stripePaymentStatus &&
    [StripePaymentStatus.PENDING, StripePaymentStatus.PROCESSING].includes(facture.stripePaymentStatus);

  const handleCreatePaymentLink = async () => {
    setLoading(true);
    setError('');

    try {
      const origin = window.location.origin;

      // Vérification de l'ID de la facture
      if (!facture.id || isNaN(Number(facture.id))) {
        setError(`ID de facture invalide: ${facture.id}`);
        setLoading(false);
        return;
      }

      console.log('🔗 Création du lien de paiement pour la facture:', facture.id);
      console.log('📍 URLs de redirection:', {
        success: `${origin}/public/payment-success?factureId=${facture.id}`,
        cancel: `${origin}/public/payment-cancel?factureId=${facture.id}`,
      });

      const response = await paymentService.createPaymentLink({
        factureId: facture.id,
        successUrl: `${origin}/public/payment-success?factureId=${facture.id}`,
        cancelUrl: `${origin}/public/payment-cancel?factureId=${facture.id}`,
      });

      console.log('✅ Lien de paiement créé:', response);

      if (onPaymentCreated) {
        onPaymentCreated();
      }

      // Afficher le lien de paiement au lieu de rediriger
      setCurrentPaymentUrl(response.paymentUrl);
      setPaymentLinkDialogOpen(true);
    } catch (err: any) {
      console.error('Erreur lors de la création du lien de paiement:', err);
      setError(
        err.response?.data?.message ||
        'Une erreur est survenue lors de la création du lien de paiement'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPaymentLink = async () => {
    setLoading(true);
    setError('');

    try {
      await paymentService.cancelPaymentLink(facture.id);

      if (onPaymentCreated) {
        onPaymentCreated();
      }

      setConfirmDialogOpen(false);
    } catch (err: any) {
      console.error('Erreur lors de l\'annulation du lien:', err);
      setError(
        err.response?.data?.message ||
        'Une erreur est survenue lors de l\'annulation du lien de paiement'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleShowPaymentLink = () => {
    if (facture.stripePaymentUrl) {
      setCurrentPaymentUrl(facture.stripePaymentUrl);
      setPaymentLinkDialogOpen(true);
    }
  };

  // Si la facture est déjà payée, ne rien afficher
  if (facture.statut === StatutFacture.PAYEE) {
    return null;
  }

  return (
    <>
      <Box>
        {canCreatePayment && (
          <Tooltip title="Générer un lien de paiement avec QR code pour cette facture">
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <QrCode2 />}
              onClick={handleCreatePaymentLink}
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #635BFF 0%, #4F46E5 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
                },
                '&.Mui-disabled': {
                  background: '#A5A5FF',
                  color: 'white',
                },
              }}
            >
              {loading ? 'Génération...' : 'Générer un lien de paiement'}
            </Button>
          </Tooltip>
        )}

        {hasPendingPayment && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={handleShowPaymentLink}
              sx={{
                background: 'linear-gradient(135deg, #635BFF 0%, #4F46E5 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
                },
              }}
            >
              Afficher le lien de paiement
            </Button>
            <Tooltip title="Annuler le lien de paiement">
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => setConfirmDialogOpen(true)}
                disabled={loading}
              >
                Annuler le lien
              </Button>
            </Tooltip>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Dialog de confirmation d'annulation */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Annuler le lien de paiement</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir annuler le lien de paiement pour cette facture ?
            Le client ne pourra plus utiliser le lien existant.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={loading}>
            Non, garder le lien
          </Button>
          <Button
            onClick={handleCancelPaymentLink}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? 'Annulation...' : 'Oui, annuler'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'affichage du lien de paiement */}
      <PaymentLinkDisplay
        open={paymentLinkDialogOpen}
        onClose={() => setPaymentLinkDialogOpen(false)}
        paymentUrl={currentPaymentUrl}
        factureNumero={facture.numero}
        montantTTC={facture.montantTTC}
      />
    </>
  );
};

export default PaymentButton;
